import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchPricesData } from '../../services/dataApi';
import { fetchPageData } from '../../services/corezoidApi';
import PageLoader from '../../components/PageLoader';
import PricesTable from '../../components/PricesTable';
import AddPriceModal from '../../components/AddPriceModal';
import SearchBar from '../../components/SearchBar';
import { searchInData } from '../../utils/searchUtils';
import useUrlPagination from '../../hooks/useUrlPagination';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useAuth } from '../../contexts/AuthContext';
import '../Page.css';

const AdminPrices = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.isAdmin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { currentPage, setCurrentPage, itemsPerPage } = useUrlPagination();
  const [pagination, setPagination] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);

  const loadData = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchPricesData(page, itemsPerPage, debouncedSearchTerm);
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    loadData(currentPage);
  }, [currentPage, loadData]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // Filter data on client side
  const filteredData = useMemo(() => {
    if (!data) return null;
    
    let prices = [];
    if (data && data.data && Array.isArray(data.data)) {
      prices = data.data;
    } else if (Array.isArray(data)) {
      prices = data;
    } else if (data && data.ops && data.ops.length > 0) {
      const firstOp = data.ops[0];
      if (firstOp.list && Array.isArray(firstOp.list)) {
        prices = firstOp.list;
      } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
        prices = firstOp.data.list;
      }
    }

    if (!searchTerm || searchTerm.trim() === '') {
      return data;
    }

    const filtered = searchInData(prices, searchTerm);
    
    if (data && data.data && Array.isArray(data.data)) {
      return { ...data, data: filtered };
    } else if (Array.isArray(data)) {
      return filtered;
    } else {
      return { ...data, ops: [{ ...data.ops[0], list: filtered }] };
    }
  }, [data, searchTerm]);

  const pricesCount = pagination?.total ?? (filteredData?.data?.length || 0);

  const generateUniqueRef = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const digits = '0123456789';
    let result = '';
    for (let i = 0; i < 9; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    // Ensure at least one digit is present
    if (!/\d/.test(result)) {
      const randomPosition = Math.floor(Math.random() * 9);
      result = result.substring(0, randomPosition) + 
               digits.charAt(Math.floor(Math.random() * digits.length)) + 
               result.substring(randomPosition + 1);
    }
    return result;
  };

  const handleAddPrice = async (priceData) => {
    try {
      // Generate a unique 9-character alphanumeric ref
      const uniqueRef = generateUniqueRef();

      // Formulate request to create price via regular API 2.0
      const ops = [{
        type: "create",
        obj: "task",
        data: priceData,
        ref: uniqueRef
      }];

      const result = await fetchPageData('prices', ops, 30, false);

      if (result.success && result.data) {
        // Check response format
        const responseData = result.data;

        // Check request_proc
        if (responseData.request_proc === 'ok' && responseData.ops && responseData.ops.length > 0) {
          const firstOp = responseData.ops[0];

          if (firstOp.proc === 'ok') {
            loadData();
            return { success: true };
          } else if (firstOp.proc === 'error') {
            const errorMsg = firstOp.description || 'Unknown error';
            return { success: false, error: errorMsg };
          }
        }

        // If response format is unexpected
        return { success: false, error: 'Unexpected response format' };
      } else {
        return { success: false, error: result.error || 'Failed to create price' };
      }
    } catch (err) {
      console.error('Error creating price:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  const buildChangedFields = (originalData, inputData, fieldMapping) => {
    const changed = {};

    Object.keys(fieldMapping).forEach(formField => {
      const possibleKeys = fieldMapping[formField];
      const newValue = inputData[formField];

      if (newValue === undefined || newValue === '') {
        return;
      }

      let keyFound = false;
      for (const key of possibleKeys) {
        if (key in originalData) {
          keyFound = true;
          if (originalData[key] !== newValue) {
            changed[key] = newValue;
          }
          break;
        }
      }

      if (!keyFound) {
        changed[formField] = newValue;
      }
    });

    return changed;
  };

  const handleEditPrice = async (priceData, originalPrice) => {
    try {
      // Get price ref for update
      const priceRef = originalPrice?.ref;
      
      if (!priceRef) {
        console.error('Price ref not found:', originalPrice);
        return { success: false, error: 'Price ref not found' };
      }

      // Get all original price data (now on top level)
      const originalData = originalPrice;
      
      // Map form fields to possible original keys
      const fieldMapping = {
        fiat: ['fiat', 'Fiat', 'FIAT'],
        gwPayIn: ['gwPayIn', 'GwPayIn', 'GWPAYIN'],
        gwPayOut: ['gwPayOut', 'GwPayOut', 'GWPAYOUT'],
        payIn: ['payIn', 'PayIn', 'PAY_IN'],
        payOut: ['payOut', 'PayOut', 'PAY_OUT']
      };
      
      const changedData = buildChangedFields(originalData, priceData, fieldMapping);
      if (Object.keys(changedData).length === 0) {
        return { success: true, skipped: true };
      }

      // Formulate request to update price via regular API 2.0
      const ops = [{
        type: "modify",
        obj: "task",
        data: changedData,
        ref: priceRef
      }];

      const result = await fetchPageData('prices', ops, 30, false);

      if (result.success && result.data) {
        // Check response format
        const responseData = result.data;
        
        // Check request_proc
        if (responseData.request_proc === 'ok' && responseData.ops && responseData.ops.length > 0) {
          const firstOp = responseData.ops[0];
          
          if (firstOp.proc === 'ok') {
            loadData();
            return { success: true };
          } else if (firstOp.proc === 'error') {
            const errorMsg = firstOp.description || 'Unknown error';
            return { success: false, error: errorMsg };
          }
        }
        
        // If response format is unexpected
        return { success: false, error: 'Unexpected response format' };
      } else {
        return { success: false, error: result.error || 'Failed to update price' };
      }
    } catch (err) {
      console.error('Error updating price:', err);
      return { success: false, error: err.message || 'An error occurred' };
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>Admin Prices</h1>
          <p>Manage rate cards in the same operational style as the rest of the admin console.</p>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Rate Cards</div>
            <div className="overview-value">{pricesCount}</div>
            <div className="overview-meta">Visible records</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Scope</div>
            <div className="overview-value">Global</div>
            <div className="overview-meta">All traders share this directory</div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading} error={error}>
          <div className="page-data">
            {data ? (
              <>
                <div className="page-section-header">
                  <div className="page-section-title">
                    <h2>Pricing Directory</h2>
                    <p>Add or edit prices.</p>
                  </div>
                  <div className="page-section-meta">
                    <div className="page-chip">Admin view</div>
                    <div className="page-chip">{searchTerm ? 'Filtered results' : 'Full directory'}</div>
                  </div>
                </div>
                <SearchBar 
                  onSearch={handleSearch}
                  value={searchTerm}
                  resultCount={pagination?.total || 0}
                  isUpdating={searchTerm !== debouncedSearchTerm}
                  placeholder="Search prices..."
                />
                <PricesTable 
                  data={filteredData} 
                  onAdd={canManage ? handleAddPrice : undefined}
                  onEdit={canManage ? handleEditPrice : undefined}
                  onAddClick={canManage ? () => setIsAddModalOpen(true) : undefined}
                pagination={pagination}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="page-placeholder">
                <p>No data received</p>
              </div>
            )}
          </div>
        </PageLoader>
        {canManage && (
          <AddPriceModal
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            onSubmit={handleAddPrice}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPrices;
